import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { shouldUseSelfAssessmentRoutes } from '../../utils/userAccessHelpers';

/**
 * SelfAssessmentRoute - Only allows users with organization package_tier to access self assessment routes
 * Redirects full accounts (without package_tier) to /dashboard
 * 
 * Migration: Phase 8.4 - Uses package_tier instead of account_type
 */
const SelfAssessmentRoute = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isSelfAssessorAccount, setIsSelfAssessorAccount] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Get fresh user data from backend
        const freshUser = await authService.getCurrentUser();
        
        if (!freshUser) {
          // No user data, redirect to signin
          setChecking(false);
          return;
        }

        // Update localStorage with fresh data
        localStorage.setItem('user', JSON.stringify(freshUser));

        // Check if user should use self assessment routes based on package_tier
        const canAccess = await shouldUseSelfAssessmentRoutes(freshUser);
        
        if (canAccess) {
          setIsSelfAssessorAccount(true);
        } else {
          // Full account (no package_tier), redirect to regular dashboard
          setIsSelfAssessorAccount(false);
        }
      } catch (error) {
        console.error('Error checking package tier:', error);
        // On error, check localStorage as fallback
        try {
          const cachedUser = authService.getUser();
          if (cachedUser) {
            const canAccess = await shouldUseSelfAssessmentRoutes(cachedUser);
            setIsSelfAssessorAccount(canAccess);
          } else {
            setIsSelfAssessorAccount(false);
          }
        } catch (e) {
          setIsSelfAssessorAccount(false);
        }
      } finally {
        setChecking(false);
      }
    };

    checkAccess();
  }, [location.pathname]);

  if (checking) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // If not a self assessor account, redirect to regular dashboard
  if (!isSelfAssessorAccount) {
    return <Navigate to="/dashboard" replace />;
  }

  // Self assessor account, allow access
  return children;
};

export default SelfAssessmentRoute;

